/* ---------------------------------------------------------------------------
 * PlatformActiveBanner
 *
 * Iter 32a — sticky banner shown ONLY to Platform Root operators when
 * they have selected a tenant via the Platform Console "Act as" button.
 *
 * Iter 32f stabilisation — banner is now demo-safe by default:
 *   - "Acting as <Tenant Name>" is the default visible label.
 *   - The raw tenant_id is HIDDEN behind a chevron toggle so screenshots
 *     and demos do not leak internal ids.
 *   - When revealed, a one-click copy button writes the id to the
 *     clipboard.
 *
 * Tenant users never render this component (the parent guards on
 * `user.is_platform_root`). Banner stays visible until "Exit context"
 * is clicked or sessionStorage idle-clears the selection.
 * --------------------------------------------------------------------------- */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatform } from '../context/PlatformContext';
import { useAuth } from '../context/AuthContext';

export default function PlatformActiveBanner() {
  const { user } = useAuth();
  const { activeTenantId, activeTenantName, clearActiveTenant } = usePlatform();
  const navigate = useNavigate();
  const [revealId, setRevealId] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!user || !user.is_platform_root) return null;
  if (!activeTenantId) return null;

  const onCopy = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(activeTenantId));
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    } catch { /* ignore */ }
  };

  return (
    <div
      data-testid="platform-active-banner"
      className="sticky top-0 z-40 w-full border-b border-amber-300/60 bg-amber-50/90 backdrop-blur supports-[backdrop-filter]:bg-amber-50/80"
      role="status"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-2 text-sm text-amber-900">
        <span aria-hidden="true">🛡️</span>
        <span className="font-medium">
          Acting as <span data-testid="platform-active-tenant-name">{activeTenantName}</span>
        </span>

        {/* Iter 32f — id hidden by default; chevron reveals it. */}
        <button
          type="button"
          data-testid="platform-active-tenant-id-toggle"
          aria-expanded={revealId}
          aria-label={revealId ? 'Hide tenant id' : 'Show tenant id'}
          onClick={() => setRevealId(v => !v)}
          className="ml-1 rounded-full px-1.5 py-0.5 text-xs text-amber-700/70 hover:bg-amber-100 hover:text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
        >
          <span aria-hidden="true">{revealId ? '▾' : '▸'}</span>
          <span className="sr-only">tenant id</span>
        </button>
        {revealId && (
          <span className="flex items-center gap-1 text-xs text-amber-700/80">
            <code
              data-testid="platform-active-tenant-id"
              className="rounded bg-amber-100/80 px-1.5 py-0.5 font-mono"
            >
              {activeTenantId}
            </code>
            <button
              type="button"
              data-testid="platform-active-tenant-id-copy"
              onClick={onCopy}
              className="rounded-full px-1.5 py-0.5 text-amber-700/80 hover:bg-amber-100 hover:text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-400"
              aria-label="Copy tenant id"
            >
              {copied ? '✓ copied' : 'copy'}
            </button>
          </span>
        )}

        <span className="ml-auto flex items-center gap-2">
          <button
            type="button"
            data-testid="platform-active-switch-btn"
            onClick={() => navigate('/platform')}
            className="rounded-full border border-amber-300 px-3 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            Switch tenant
          </button>
          <button
            type="button"
            data-testid="platform-active-exit-btn"
            onClick={clearActiveTenant}
            className="rounded-full border border-transparent bg-amber-900 px-3 py-0.5 text-xs font-medium text-amber-50 hover:bg-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-700"
          >
            Exit context
          </button>
        </span>
      </div>
    </div>
  );
}
